import React, { Component, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import './styles/App.css';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

import HomePageHeader from './components/homepage/HomePageHeader';
import Footer from './shared/Footer';
import LandingPage from './views/LandingPage';
import { tabs } from './routes';
import { useAuthContext } from "./components/auth/AuthContext";
import Auth from "./components/auth/Auth";
import Callback from './components/auth/Callback';
import { ThemeProvider } from 'styled-components';
import mainTheme from './theme/main';
import Loading from './views/Loading';
import ExpireSession from './views/ExpireSession';
import { PREVIOUSLY_IN_INK } from './utils/auth-jwt';
import HomePageSidebar from './components/homepage/HomePageSidebar';
import HomePage from './views/HomePage';
import { sidebarIcons } from './components/homepage/utils/sidebarData';

const getActiveIconFromPath = (pathname) => {
  const matched = sidebarIcons.find(
    (item) => pathname === item.route || pathname.startsWith(`${item.route}/`)
  );
  return matched ? matched.id : 'home';
};

const RenderLanding = () => {
  const { isLoading } = useAuthContext();

  if (isLoading) {
    return (
      <Loading />
    )
  }

  return (
    <div>
      <LandingPage />
      <Routes>
        <Route path='/callback' element={<Callback />} />
        <Route path='*' element={null} />
      </Routes>
    </div>
  );
};

const RenderApp = () => {
  const location = useLocation();
  const isSwipeable = location.pathname.includes("swipeable");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSidebarIcon, setActiveSidebarIcon] = useState(() =>
    getActiveIconFromPath(location.pathname)
  );

  useEffect(() => {
    setActiveSidebarIcon(getActiveIconFromPath(location.pathname));
  }, [location.pathname]);

  return (
      <div className="d-flex">
        <HomePageSidebar
          collapsed={sidebarCollapsed}
          activeIcon={activeSidebarIcon}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          onIconClick={setActiveSidebarIcon}
        />
        <Container
          fluid
          className={[
            "App-layout",
            isSwipeable ? "swipeable-container" : "",
          ].join(" ").trim()}
        >
          <Row>
            <Col className="App-header">
              <HomePageHeader />
            </Col>
          </Row>
          <Row>
            <Col className="App-body">
              <Routes>
                {tabs.map((route, idx) => (
                  <Route key={idx} path={route.path} element={<route.component />} />
                ))}
                <Route path='*' element={<HomePage />} />
              </Routes>
            </Col>
          </Row>
          <Row className="App-footer App-footerRow">
            <Col className="App-footer">
              <Footer />
            </Col>
          </Row>
        </Container>
      </div>
    );
};

const CheckAuth = () => {
  const { isLoggedIn, loggedIn, isTokenValid } = useAuthContext();
  const [showExpire, setShowExpire] = useState(false);
  const tokenStillValid = isTokenValid();

  useEffect(() => {
    loggedIn();
    const intervalId = setInterval(() => {
      loggedIn();
    }, 600000);

    return () => {
      clearInterval(intervalId);
    };
  }, [loggedIn]);

  useEffect(() => {
    const checkToken = () => {
      const previouslyInInk = window?.localStorage?.getItem(PREVIOUSLY_IN_INK);

      if (previouslyInInk === 'true' && !tokenStillValid) {
        setShowExpire(true);
      }
    };

    checkToken();
    window.addEventListener('focus', checkToken);

    return () => window.removeEventListener('focus', checkToken);
  }, [tokenStillValid]);

  if (showExpire) {
    return <ExpireSession />;
  }

  if (isLoggedIn && /access_token|id_token|error/.test(window.location.hash)) {
    window.location.href = '/home';
  }

  if (isLoggedIn) {
    return <RenderApp />
  }

  return <RenderLanding />
};

const toastOptions: ToastOptions = {
  duration: 4000,
  style: {
    background: '#333',
    color: '#fff',
    padding: '12px 16px',
    borderRadius: '4px',
    fontSize: '14px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  success: {
    duration: 3000,
    style: {
      background: '#dff0d8',
      color: '#3c763d',
    },
    iconTheme: {
      primary: '#3c763d',
      secondary: '#dff0d8',
    },
  },
  error: {
    duration: 4000,
    style: {
      background: '#f2dede',
      color: '#a94442',
    },
    iconTheme: {
      primary: '#a94442',
      secondary: '#f2dede',
    },
  },
};
class App extends Component {
  render() {
    return (
      <ThemeProvider theme={mainTheme}>
        <Toaster position='top-right' toastOptions={toastOptions} />
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="App">
            <Auth>
              <CheckAuth />
            </Auth>
          </div>
        </Router>
      </ThemeProvider>
    );
  }
}

export default App;